import { useAuth } from "@/_core/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { P_ASSISTANT_USE, P_INBOX_VIEW, P_REPORTS_VIEW_INBOX } from "@shared/permissions";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Logo, LogoMark } from "@/components/Logo";
import { getLoginUrl } from "@/const";
import { useIsMobile } from "@/hooks/useMobile";
import {
  BarChart2,
  ChevronRight,
  FileText,
  Filter,
  Flame,
  GitBranch,
  Inbox,
  LayoutDashboard,
  LayoutTemplate,
  Link2,
  ListOrdered,
  LogOut,
  Mail,
  MessageCircle,
  PanelLeft,
  Radio,
  Settings,
  Tag,
  UserCheck,
  Users,
  Network,
  Package,
  Shield,
  Zap,
  UserPlus,
  Brain,
  Target,
  Send,
  ListChecks,
  FileSignature,
  Briefcase,
  Trophy,
  Percent,
  Building2,
  Bot,
  Plug,
  Megaphone,
  Rocket,
  LineChart,
  BookOpen,
  Instagram,
  Headset,
  Settings2,
  MessageSquare,
  CalendarDays,
  BarChart3,
  Sparkles,
  Receipt,
} from "lucide-react";
import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { DashboardLayoutSkeleton } from "./DashboardLayoutSkeleton";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { Button } from "./ui/button";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { evaluateUnreadDelta } from "@/lib/unread-toast";
import { CLIENT_VERSION } from "@/contexts/UpdateContext";
import type { LucideIcon } from "lucide-react";
import { toast } from "sonner";

// ─── Nav structure ────────────────────────────────────────────────────────────

type NavLeaf = {
  kind: "item";
  icon: LucideIcon;
  label: string;
  path: string;
  badge?: boolean;
  permission?: string;
};

type NavGroup = {
  kind: "group";
  icon: LucideIcon;
  label: string;
  /** All paths that belong to this group (for auto-expand + active detection) */
  paths: string[];
  items: { icon: LucideIcon; label: string; path: string; permission?: string }[];
};

type NavNode = NavLeaf | NavGroup;

// ─────────────────────────────────────────────────────────────────────────────
// Navigation map
//
// Arquitetura orientada ao uso real (SaaS multicanal):
//   1. Operação do dia a dia  → Dashboard, Atendimento
//   2. Audiência              → Contatos
//   3. Canais de comunicação  → WhatsApp, E-mail   (operação, não config técnica)
//   4. Recursos transversais  → Campanhas, Automação, Fluxos
//   5. Dados                  → Relatórios
//   6. Administração          → Configurações (inclui Integrações técnicas)
//
// Regra: credenciais, API keys e webhooks ficam em Configurações > Integrações.
// Canais expõem apenas ferramentas operacionais (templates, sandbox, opt-in...).
// Escala futura: cada novo canal (Instagram, Ligações) vira um NavGroup aqui.
// ─────────────────────────────────────────────────────────────────────────────
const navigation: NavNode[] = [
  // ── Operação ────────────────────────────────────────────────────────────────
  {
    kind: "item",
    icon: LayoutDashboard,
    label: "Dashboard",
    path: "/",
    permission: "dashboard:view",
  },
  {
    kind: "item",
    icon: Inbox,
    label: "Atendimento",
    path: "/inbox",
    badge: true,
    permission: "inbox:view",
  },
  {
    kind: "item",
    icon: BarChart2,
    label: "Relatórios de Atendimento",
    path: "/inbox/reports",
    permission: "reports:view_inbox",
  },
  // Chat que executa e ensina. Fica no topo porque é o atalho para tudo o
  // que está abaixo — e o dock flutuante leva para cá em tela cheia.
  {
    kind: "item",
    icon: Sparkles,
    label: "Assistente",
    path: "/assistente",
    permission: P_ASSISTANT_USE,
  },

  // ── IA (Inteligência + Agentes + Métricas) ──────────────────────────────────
  // F1.1+: base de conhecimento, registry de agentes IA, métricas.
  // CHAVES BYOK saíram daqui — viraram sub-integração em Configurações
  // (são integração com provider externo, não feature de IA própria).
  {
    kind: "group",
    icon: Brain,
    label: "Inteligência",
    paths: ["/intelligence", "/agents", "/routing-metrics"],
    items: [
      { icon: Brain,    label: "Base de Conhecimento", path: "/intelligence",    permission: "whatsapp:knowledge_base" },
      { icon: Bot,      label: "Agentes IA",            path: "/agents",          permission: "ai:agents_manage" },
      { icon: BarChart2, label: "Métricas do Routing", path: "/routing-metrics", permission: "ai:routing_engine_manage" },
    ],
  },

  // ── Audiência ───────────────────────────────────────────────────────────────
  {
    kind: "group",
    icon: Users,
    label: "Contatos",
    paths: ["/contacts", "/contacts/hierarchy", "/tags", "/segments", "/lead-scoring"],
    items: [
      { icon: Users,   label: "Todos os Contatos", path: "/contacts",          permission: "contacts:view" },
      { icon: Network, label: "Hierarquia",         path: "/contacts/hierarchy", permission: "contacts:view" },
      { icon: Filter,  label: "Segmentos",          path: "/segments",           permission: "contacts:manage_segments" },
      { icon: Tag,     label: "Tags",               path: "/tags",              permission: "contacts:manage_tags" },
      { icon: Flame,   label: "Lead Scoring",       path: "/lead-scoring",      permission: "contacts:lead_scoring" },
    ],
  },

  // ── Canais ──────────────────────────────────────────────────────────────────
  // WhatsApp: dashboard, relatórios, transmissão, automações, templates, fluxos, sandbox, captação.
  {
    kind: "group",
    icon: MessageCircle,
    label: "WhatsApp",
    paths: [
      "/whatsapp", "/whatsapp/reports",
      "/broadcast", "/templates", "/whatsapp-test", "/optin", "/whatsapp-flows",
      "/automations", "/sequences", "/auto-assignment", "/ai-insights",
    ],
    items: [
      { icon: LayoutDashboard, label: "Dashboard",        path: "/whatsapp",         permission: "dashboard:view" },
      { icon: BarChart2,       label: "Relatórios",       path: "/whatsapp/reports", permission: "reports:view_whatsapp" },
      { icon: Radio,         label: "Transmissão",       path: "/broadcast",       permission: "whatsapp:view_broadcasts" },
      { icon: Zap,           label: "Automações",        path: "/automations",     permission: "whatsapp:manage_automations" },
      { icon: ListOrdered,   label: "Sequências",        path: "/sequences",       permission: "whatsapp:manage_sequences" },
      { icon: FileText,      label: "Templates",         path: "/templates",       permission: "whatsapp:manage_templates" },
      { icon: GitBranch,     label: "Fluxos",            path: "/whatsapp-flows",  permission: "whatsapp:manage_flows" },
      { icon: UserCheck,     label: "Auto-atribuição",   path: "/auto-assignment", permission: "whatsapp:auto_assignment" },
      { icon: BarChart2,     label: "Insights de IA",       path: "/ai-insights",  permission: "whatsapp:ai_insights" },
      { icon: MessageCircle, label: "Sandbox / Teste",   path: "/whatsapp-test",   permission: "whatsapp:sandbox" },
      { icon: Link2,         label: "Links de Captação", path: "/optin",           permission: "whatsapp:optin" },
    ],
  },
  // E-mail: dashboard, modelos, fluxos e IA.
  {
    kind: "group",
    icon: Mail,
    label: "E-mail",
    paths: ["/emails", "/flows"],
    items: [
      { icon: Inbox,           label: "Dashboard",            path: "/emails",             permission: "email:view_campaigns" },
      { icon: LayoutTemplate,  label: "Modelos Prontos",      path: "/emails/templates",   permission: "email:manage_templates" },
      { icon: GitBranch,       label: "Fluxos de Automação",  path: "/flows",              permission: "email:manage_flows" },
    ],
  },

  // Anúncios: tráfego pago na Meta. Canal de aquisição, não de mensagem — por
  // isso grupo próprio, e não um item dentro de WhatsApp.
  // Instagram: canal de comunicação. As DMs caem no Atendimento junto com o
  // WhatsApp (caixa unificada); o que ganha tela própria é o que NÃO é
  // conversa — comentário em post é público e tem fluxo de trabalho diferente.
  {
    kind: "group",
    icon: Instagram,
    label: "Redes sociais",
    paths: ["/instagram", "/social"],
    items: [
      // Agendador: Instagram, Facebook, TikTok, YouTube e LinkedIn. O grupo
      // nasceu do Instagram (comentários e automações seguem aqui).
      { icon: CalendarDays, label: "Publicações", path: "/social", permission: "social:publish" },
      { icon: BarChart3, label: "Insights", path: "/social/insights", permission: "social:view_insights" },
      // Links de captação: post → clique → lead no CRM, por rede.
      // "Leads das redes", e não "Links de captação": esse nome já é do
      // gerador de opt-in do WhatsApp (/optin), que é outra coisa.
      { icon: Link2, label: "Leads das redes", path: "/social/captacao", permission: "social:publish" },
      { icon: UserPlus, label: "Contas", path: "/social/contas", permission: "social:manage_accounts" },
      { icon: MessageSquare, label: "Comentários", path: "/instagram/comments", permission: "instagram:view_comments" },
      // Automações comentário → DM. Quem vê comentários vê as automações e o
      // log; criar/editar exige `instagram:manage_automations` (a própria
      // tela esconde os botões).
      { icon: Zap, label: "Automações", path: "/instagram/automations", permission: "instagram:view_comments" },
      // Sem `permission`: o manual precisa estar visível para quem AINDA não
      // conectou nada. Exigir a permissão de conexão esconderia justamente de
      // quem está tentando descobrir o que fazer. A própria página filtra o que
      // mostrar conforme o que a pessoa pode.
      { icon: BookOpen, label: "Como conectar", path: "/conectar-meta" },
    ],
  },

  {
    kind: "group",
    icon: Megaphone,
    label: "Anúncios",
    paths: ["/ads", "/ads/campaigns", "/ads/new", "/ads/atribuicao"],
    items: [
      { icon: Megaphone, label: "Meta Ads", path: "/ads", permission: "ads:view" },
      { icon: Rocket, label: "Impulsionar", path: "/ads/new", permission: "ads:manage" },
      { icon: LineChart, label: "Atribuição", path: "/ads/atribuicao", permission: "ads:view" },
      { icon: BookOpen, label: "Como conectar", path: "/conectar-meta" },
    ],
  },

  // Cobrança: quanto a Meta cobra pelas mensagens. Item solto, e não uma aba
  // de Configurações, porque quem precisa do número (dono, financeiro) não é
  // quem mexe em credencial — e a permissão é justamente essa separação.
  {
    kind: "item",
    icon: Receipt,
    label: "Cobrança",
    path: "/cobranca",
    permission: "billing:view",
  },

  // ── CRM (funil de vendas) ───────────────────────────────────────────────────
  {
    kind: "group",
    icon: Target,
    label: "CRM",
    paths: ["/crm", "/crm/prospecting", "/crm/deals", "/crm/tasks", "/crm/proposals", "/crm/reports", "/crm/products", "/crm/sellers", "/crm/commissions", "/crm/organizations", "/agenda"],
    items: [
      { icon: Send,        label: "Prospecção",         path: "/crm/prospecting",   permission: "crm:prospecting_view" },
      { icon: Briefcase,   label: "Oportunidades",      path: "/crm/deals",         permission: "crm:deals_view" },
      { icon: CalendarDays, label: "Agenda",            path: "/agenda",            permission: "agenda:view" },
      { icon: ListChecks,  label: "Tarefas",            path: "/crm/tasks",         permission: "crm:tasks_view" },
      { icon: FileSignature, label: "Propostas",        path: "/crm/proposals",     permission: "crm:proposals_view" },
      { icon: Building2,   label: "Organizações",       path: "/crm/organizations", permission: "crm:deals_view" },
      { icon: Package,     label: "Produtos",           path: "/crm/products",      permission: "crm:products_view" },
      { icon: Trophy,      label: "Vendedores",         path: "/crm/sellers",       permission: "crm:sellers_view" },
      { icon: Percent,     label: "Comissões",          path: "/crm/commissions",   permission: "crm:commissions_view" },
      { icon: BarChart2,   label: "Relatórios de vendas", path: "/crm/reports",     permission: "crm:reports_view" },
    ],
  },

  // ── Copiloto de call ────────────────────────────────────────────────────────
  // Sugestao de script ao vivo na reuniao. Vive fora do CRM porque quem usa
  // (o vendedor na call) e quem configura (playbook, politica) sao papeis
  // diferentes, e a tela de configuracao e o ponto de partida para conectar
  // a extensao — precisa estar visivel para quem so tem `copilot:use`.
  {
    kind: "group",
    icon: Headset,
    label: "Copiloto de call",
    paths: ["/copiloto"],
    items: [
      { icon: Settings2, label: "Configurações", path: "/copiloto",          permission: "copilot:use" },
      { icon: BarChart2, label: "Insights",      path: "/copiloto/insights", permission: "copilot:use" },
    ],
  },

];

// Flat map path → label for the header title
function buildLabelMap(): Record<string, string> {
  const map: Record<string, string> = {};
  for (const node of navigation) {
    if (node.kind === "item") {
      map[node.path] = node.label;
    } else {
      node.items.forEach((i) => { map[i.path] = i.label; });
    }
  }
  // Extra entries for pages not in the nav but still reachable
  map["/settings"]          = "Configurações";
  map["/users"]             = "Usuários";
  map["/roles"]             = "Perfis de Acesso";
  map["/whatsapp-status"]   = "Status da Integração";

  map["/intelligence"]       = "Inteligência da Empresa";
  map["/emails/create"]      = "Criar E-mail";
  map["/flows/create"]      = "Criar Fluxo";
  map["/broadcast/create"]  = "Nova Transmissão";

  map["/auto-assignment"]   = "Auto-atribuição";
  map["/ai-insights"]       = "Insights de IA";
  map["/whatsapp"]          = "Dashboard do WhatsApp";
  map["/whatsapp/reports"]  = "Relatórios do WhatsApp";
  map["/admin"]             = "Administração";
  map["/progress"]          = "Progresso do Desenvolvimento";
  map["/message-tracer"]    = "Message Tracer";

  map["/crm"]               = "CRM";
  map["/crm/prospecting"]   = "Prospecção";
  map["/crm/deals"]         = "Oportunidades";
  map["/crm/tasks"]         = "Tarefas";
  map["/crm/proposals"]     = "Propostas";
  map["/agenda"]            = "Agenda";
  map["/crm/reports"]       = "Relatórios de vendas";
  map["/crm/products"]      = "Produtos";
  map["/crm/products/compare"] = "Comparar produtos";
  map["/crm/sellers"]       = "Vendedores";
  map["/crm/sellers/compare"] = "Comparar vendedores";
  map["/crm/commissions"]   = "Comissões";
  map["/crm/organizations"] = "Organizações";
  return map;
}
const labelMap = buildLabelMap();

// ─── Layout constants ─────────────────────────────────────────────────────────
const SIDEBAR_WIDTH_KEY = "sidebar-width";
const DEFAULT_WIDTH = 240;
const MIN_WIDTH = 200;
const MAX_WIDTH = 320;

// ─── Root export ──────────────────────────────────────────────────────────────
export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();
  const isMobile = useIsMobile();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) return <DashboardLayoutSkeleton />;

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <div className="flex flex-col items-center gap-4">
            <LogoMark size={56} />
            <h1 className="font-display text-3xl font-semibold tracking-tight text-center">
              <span style={{ color: "var(--brand-teal)" }}>Lead</span>{" "}
              <span style={{ color: "var(--brand-fog)", fontWeight: 400 }}>Conversa</span>
            </h1>
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              Plataforma de conversação e marketing. Converse, qualifique e converta. Faça login para continuar.
            </p>
          </div>
          <Button onClick={() => { window.location.href = getLoginUrl(); }} size="lg" className="w-full">
            Entrar no sistema
          </Button>
        </div>
      </div>
    );
  }

  // Redirect to onboarding if not completed
  if (!(user as any).onboardingCompleted) {
    window.location.href = "/onboarding";
    return <DashboardLayoutSkeleton />;
  }

  return (
    <SidebarProvider defaultOpen={!isMobile} style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}>
      <DashboardLayoutContent setSidebarWidth={setSidebarWidth}>
        {children}
      </DashboardLayoutContent>
    </SidebarProvider>
  );
}

// ─── Inner layout ─────────────────────────────────────────────────────────────
function DashboardLayoutContent({
  children,
  setSidebarWidth,
}: {
  children: React.ReactNode;
  setSidebarWidth: (w: number) => void;
}) {
  const { user, logout } = useAuth();
  const { can } = usePermissions();
  const [location, setLocation] = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();
  // Em mobile o sidebar abre via Sheet (controlado por openMobile, não pelo
  // state global). Forçar "expanded" em mobile para que labels apareçam
  // quando o usuário abre o menu — sem isso ficaria só ícones.
  const isCollapsed = !isMobile && state === "collapsed";
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Filtrar navegação por permissões
  const filteredNavigation = useMemo(() => {
    return navigation.reduce<NavNode[]>((acc, node) => {
      if (node.kind === "item") {
        if (!node.permission || can(node.permission)) acc.push(node);
      } else {
        const filteredItems = node.items.filter((item) => !item.permission || can(item.permission));
        if (filteredItems.length > 0) {
          const filteredPaths = filteredItems.map((i) => i.path);
          acc.push({ ...node, items: filteredItems, paths: filteredPaths });
        }
      }
      return acc;
    }, []);
  }, [can]);

  // `enabled` obrigatório: o layout monta em TODA página, e estas duas
  // procedures são protegidas por permissão. Sem o gate, quem não tem
  // inbox:view levava um toast "Sem permissão: inbox:view" a cada 30s, em
  // qualquer tela — e reports:view_inbox a cada 60s.
  const { data: unreadData } = trpc.inbox.getUnreadCount.useQuery(undefined, {
    enabled: can(P_INBOX_VIEW),
    refetchInterval: 30000,
  });
  const unreadCount = unreadData?.count ?? 0;
  const prevUnreadRef = useRef<number | null>(null);

  useEffect(() => {
    // `unreadData` (não `unreadCount`) é o que decide: o `?? 0` acima existe
    // para o badge e, se virasse referência antes da query responder, a
    // primeira resposta real seria lida como "N mensagens novas" — era o
    // toast fantasma que aparecia a cada carregamento de página.
    const { notifyCount, nextPrevious } = evaluateUnreadDelta({
      previous: prevUnreadRef.current,
      current: unreadData?.count,
      onInbox: location.startsWith("/inbox"),
    });
    prevUnreadRef.current = nextPrevious;
    if (notifyCount === null) return;
    toast.info(
      `${notifyCount} nova${notifyCount > 1 ? "s" : ""} mensagem${notifyCount > 1 ? "ns" : ""} na Caixa de Entrada`,
      {
        action: { label: "Ver", onClick: () => setLocation("/inbox") },
        duration: 6000,
      },
    );
  }, [unreadData, location, setLocation]);

  const { data: unassignedData } = trpc.inboxReport.unassigned.useQuery(undefined, {
    enabled: can(P_REPORTS_VIEW_INBOX),
    refetchInterval: 60000,
  });
  const unassignedCount = unassignedData ?? 0;

  // Which groups are open (identified by label)
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    // Auto-open the group that contains the current path
    const initial: Record<string, boolean> = {};
    for (const node of navigation) {
      if (node.kind === "group") {
        initial[node.label] = node.paths.some((p) =>
          location === p || location.startsWith(p + "/")
        );
      }
    }
    return initial;
  });

  // Auto-expand group when navigation changes
  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      for (const node of navigation) {
        if (node.kind === "group") {
          if (node.paths.some((p) => location === p || location.startsWith(p + "/"))) {
            next[node.label] = true;
          }
        }
      }
      return next;
    });
  }, [location]);

  // Auto-collapse sidebar on Inbox page for more chat space
  useEffect(() => {
    if (location === "/inbox" && !isCollapsed) {
      toggleSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (isCollapsed) setIsResizing(false);
  }, [isCollapsed]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const sidebarLeft = sidebarRef.current?.getBoundingClientRect().left ?? 0;
      const newWidth = e.clientX - sidebarLeft;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) setSidebarWidth(newWidth);
    };
    const handleMouseUp = () => setIsResizing(false);
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, setSidebarWidth]);

  const toggle = (label: string) =>
    setOpenGroups((prev) => {
      const isClosing = prev[label];
      if (isClosing) return { ...prev, [label]: false };
      // Fechar todos e abrir apenas o clicado (accordion)
      const next: Record<string, boolean> = {};
      for (const key of Object.keys(prev)) next[key] = false;
      next[label] = true;
      return next;
    });

  const isItemActive = (path: string) =>
    location === path || (path !== "/" && location.startsWith(path + "/"));

  // Active page label for the header
  // Handles dynamic routes like /emails/:id/editor, /emails/:id, /flows/:id
  const resolveActiveLabel = (): string => {
    if (labelMap[location]) return labelMap[location];
    // Match /emails/:id/editor
    if (/^\/emails\/\d+\/editor$/.test(location)) return "Editor de E-mail";
    // Match /emails/templates/:id/edit (edição de template no mesmo editor)
    if (/^\/emails\/templates\/\d+\/edit$/.test(location)) return "Editor de Template";
    // Match /flows/:id
    if (/^\/flows\/\d+$/.test(location)) return "Editor de Fluxo";
    // Fallback to prefix match
    return labelMap[Object.keys(labelMap).find((p) => p !== "/" && location.startsWith(p + "/")) ?? ""] ?? "";
  };
  const activeLabel = resolveActiveLabel();

  // ── Render helpers ───────────────────────────────────────────────────────────

  function renderLeaf(node: NavLeaf) {
    const active = isItemActive(node.path);
    const showBadge = node.badge && unreadCount > 0;
    const showUnassignedBadge = node.badge && unassignedCount > 0;
    return (
      <SidebarMenuItem key={node.path}>
        <SidebarMenuButton
          isActive={active}
          onClick={() => setLocation(node.path)}
          tooltip={node.label}
          className={`h-9 transition-all font-normal cursor-pointer text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent ${active ? "bg-sidebar-accent text-sidebar-foreground font-medium shadow-[inset_3px_0_0_var(--brand-accent)]" : ""}`}
        >
          <node.icon className={`h-4 w-4 shrink-0 ${active ? "text-[color:var(--brand-accent)]" : ""}`} />
          <span className="flex-1 truncate">{node.label}</span>
          {!isCollapsed && (showBadge || showUnassignedBadge) && (
            <span className="ml-auto flex flex-col items-end gap-0.5">
              {showBadge && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
              {showUnassignedBadge && (
                <span className="flex h-4 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[9px] font-semibold text-white whitespace-nowrap">
                  {unassignedCount > 99 ? "99+" : unassignedCount} sem agente
                </span>
              )}
            </span>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  function renderGroup(node: NavGroup) {
    const groupActive = node.paths.some(isItemActive);
    const isOpen = openGroups[node.label] ?? false;

    if (isCollapsed) {
      // In icon mode: clicking the group icon navigates to first child
      return (
        <SidebarMenuItem key={node.label}>
          <SidebarMenuButton
            isActive={groupActive}
            onClick={() => setLocation(node.items[0].path)}
            tooltip={node.label}
            className={`h-9 font-normal cursor-pointer text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent ${groupActive ? "bg-sidebar-accent text-sidebar-foreground font-medium shadow-[inset_3px_0_0_var(--brand-accent)]" : ""}`}
          >
            <node.icon className={`h-4 w-4 shrink-0 ${groupActive ? "text-[color:var(--brand-accent)]" : ""}`} />
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    }

    return (
      <Collapsible key={node.label} open={isOpen} onOpenChange={() => toggle(node.label)}>
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton
              isActive={groupActive && !isOpen}
              tooltip={node.label}
              className={`h-9 font-normal cursor-pointer text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent ${groupActive && !isOpen ? "bg-sidebar-accent text-sidebar-foreground font-medium shadow-[inset_3px_0_0_var(--brand-accent)]" : ""}`}
            >
              <node.icon className={`h-4 w-4 shrink-0 ${groupActive ? "text-[color:var(--brand-accent)]" : ""}`} />
              <span className="flex-1 truncate">{node.label}</span>
              <ChevronRight
                className={`h-3.5 w-3.5 shrink-0 text-sidebar-foreground/40 transition-transform duration-150 ${isOpen ? "rotate-90" : ""}`}
              />
            </SidebarMenuButton>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <SidebarMenuSub className="ml-3 border-l border-sidebar-border pl-2 py-0.5">
              {node.items.map((item) => {
                const active = isItemActive(item.path);
                return (
                  <SidebarMenuSubItem key={item.path}>
                    <SidebarMenuSubButton
                      isActive={active}
                      onClick={() => setLocation(item.path)}
                      className={`h-8 text-[13px] font-normal cursor-pointer text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent ${active ? "bg-sidebar-accent text-sidebar-foreground font-medium shadow-[inset_3px_0_0_var(--brand-accent)]" : ""}`}
                    >
                      <item.icon className={`h-3.5 w-3.5 shrink-0 ${active ? "text-[color:var(--brand-accent)]" : ""}`} />
                      <span className="truncate">{item.label}</span>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                );
              })}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    );
  }

  // ── Main render ──────────────────────────────────────────────────────────────
  // Em mobile, o componente <Sidebar> da shadcn renderiza como <Sheet> com
  // overlay próprio — não precisamos de wrapper "fixed inset-0" nem overlay
  // custom aqui (causavam preto/50 permanente cobrindo a tela em <768px).
  return (
    <>
      <div className="relative" ref={sidebarRef}>
        <Sidebar collapsible="icon" className="border-r-0" disableTransition={isResizing}>
          {/* Header */}
          <SidebarHeader className="h-16 justify-center border-b border-sidebar-border">
            <div className={`flex items-center w-full ${isCollapsed ? "justify-center px-0" : "justify-between px-2"}`}>
              {!isCollapsed && (
                <Logo size={30} variant="onDark" className="min-w-0" />
              )}
              <button
                onClick={toggleSidebar}
                className="h-8 w-8 flex items-center justify-center hover:bg-sidebar-accent rounded-lg transition-colors focus:outline-none shrink-0 text-sidebar-foreground/70"
                aria-label="Toggle navigation"
              >
                <PanelLeft className="h-4 w-4" />
              </button>
            </div>
          </SidebarHeader>

          {/* Content */}
          <SidebarContent className="gap-0 py-2">
            {/* Workspace atual + troca entre contas vinculadas. Escondido no
                modo colapsado — o nome do tenant é o conteúdo, sem ele o botão
                viraria um ícone ambíguo. */}
            {!isCollapsed && (
              <div className="px-2 pb-1">
                <WorkspaceSwitcher />
              </div>
            )}
            <SidebarMenu className="px-2 gap-0.5">
              {filteredNavigation.map((node) => {
                if (node.kind === "item") return renderLeaf(node);
                return renderGroup(node);
              })}
            </SidebarMenu>
          </SidebarContent>

          {/* Footer: user */}
          <SidebarFooter className="p-3 border-t border-sidebar-border">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-sidebar-accent transition-colors w-full text-left focus:outline-none">
                  <Avatar className="h-8 w-8 border border-sidebar-border shrink-0">
                    <AvatarFallback className="text-xs font-medium bg-primary text-white">
                      {user?.name?.charAt(0).toUpperCase() ?? "U"}
                    </AvatarFallback>
                  </Avatar>
                  {!isCollapsed && (
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-sidebar-foreground truncate leading-none">
                        {user?.name || "Usuário"}
                      </p>
                      <p className="text-xs text-sidebar-foreground/50 truncate mt-1">
                        {user?.email || "Admin"}
                      </p>
                    </div>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {can("settings:view") && (
                  <DropdownMenuItem
                    onClick={() => setLocation("/settings")}
                    className="cursor-pointer"
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Configurações</span>
                  </DropdownMenuItem>
                )}
                {can("users:view") && (
                  <DropdownMenuItem
                    onClick={() => setLocation("/settings?tab=users")}
                    className="cursor-pointer"
                  >
                    <Users className="mr-2 h-4 w-4" />
                    <span>Usuários</span>
                  </DropdownMenuItem>
                )}
                {/* Fora do gate settings:view de propósito: usuário comum com a
                    permissão de MCP conecta a própria IA sem ver Configurações. */}
                {can("settings:mcp_tokens") && (
                  <DropdownMenuItem
                    onClick={() => setLocation("/conectar-ia")}
                    className="cursor-pointer"
                  >
                    <Plug className="mr-2 h-4 w-4" />
                    <span>Conectar IA</span>
                  </DropdownMenuItem>
                )}
                {/* Sem gate de permissão: qualquer usuário gerencia os
                    próprios vínculos de conta (feature pessoal, cross-tenant). */}
                <DropdownMenuItem
                  onClick={() => setLocation("/contas-vinculadas")}
                  className="cursor-pointer"
                >
                  <Link2 className="mr-2 h-4 w-4" />
                  <span>Contas vinculadas</span>
                </DropdownMenuItem>
                {user?.isSuperAdmin && (
                  <DropdownMenuItem
                    onClick={() => setLocation("/super-admin")}
                    className="cursor-pointer"
                  >
                    <Shield className="mr-2 h-4 w-4" />
                    <span>Super Admin</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {!isCollapsed && (
              <p
                className="mt-2 px-2 text-[10px] text-sidebar-foreground/40 tabular-nums tracking-wide select-text"
                title={`Build: ${CLIENT_VERSION.commitSha}`}
              >
                v{CLIENT_VERSION.version} · {CLIENT_VERSION.commitSha}
              </p>
            )}
          </SidebarFooter>
        </Sidebar>

        {/* Resize handle */}
        <div
          className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/20 transition-colors ${isCollapsed ? "hidden" : ""}`}
          onMouseDown={() => { if (!isCollapsed) setIsResizing(true); }}
          style={{ zIndex: 50 }}
        />
      </div>

      <SidebarInset>
        {/* Mobile header */}
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-3 backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg" />
              <span className="font-medium text-sm">{activeLabel || "Menu"}</span>
            </div>
          </div>
        )}
        <main className={cn("flex-1", location === "/inbox" || location.startsWith("/assistente") ? "p-0" : "p-5 md:p-6")}>{children}</main>
      </SidebarInset>
    </>
  );
}
